/**
 * Read a PDF's page count and draw its first page as a card image, in the
 * browser, before upload - the PDF twin of `measure()` in MediaUploadDialog.
 *
 * PDF.js is imported LAZILY: it is ~1 MB with its worker, and only the
 * Information page ever needs it. The worker is a same-origin file emitted by
 * Vite (`?worker`), so the CSP needs nothing new (`worker-src` falls back to
 * `script-src 'self'`). `isEvalSupported: false` keeps PDF.js off its
 * `new Function` font path - a PDF is someone else's file.
 *
 * Never rejects: a PDF the browser cannot read still uploads, it just lands
 * without a page count or an image (the server re-checks the bytes anyway).
 */

export interface MeasuredPdf {
  pageCount: number | null;
  /** Page 1 as a JPEG data URI, sized for a rail card. */
  posterDataUrl: string | null;
}

/** Wide enough for a 2x card; small enough for the server's poster field. */
export const PDF_POSTER_WIDTH = 640;
/** The server's `posterFrom` refuses a data URI longer than this. */
export const POSTER_MAX_CHARS = 200_000;

export type PdfMeasurer = (file: File) => Promise<MeasuredPdf>;

let workerPort: Worker | null = null;

export const measurePdf: PdfMeasurer = async (file) => {
  const none: MeasuredPdf = { pageCount: null, posterDataUrl: null };
  try {
    const [pdfjs, { default: PdfWorker }] = await Promise.all([
      import("pdfjs-dist"),
      import("pdfjs-dist/build/pdf.worker.min.mjs?worker"),
    ]);
    // A port, via Vite's `?worker`: the same in dev and in the build (a
    // `?url` import of the `.mjs` is answered with its SOURCE by the dev
    // server). One worker for the page's life; documents reuse it.
    workerPort ??= new PdfWorker();
    pdfjs.GlobalWorkerOptions.workerPort = workerPort;
    const data = new Uint8Array(await file.arrayBuffer());
    const doc = await pdfjs.getDocument({ data, isEvalSupported: false })
      .promise;
    try {
      const pageCount = doc.numPages || null;
      const page = await doc.getPage(1);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({
        scale: PDF_POSTER_WIDTH / base.width,
      });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext("2d");
      if (!ctx) return { pageCount, posterDataUrl: null };
      // A transparent page would export black in a JPEG.
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport }).promise;
      return { pageCount, posterDataUrl: jpegUnder(canvas, POSTER_MAX_CHARS) };
    } finally {
      void doc.destroy();
    }
  } catch {
    return none;
  }
};

/** The best JPEG quality that still fits; null if even the lowest does not. */
export function jpegUnder(
  canvas: HTMLCanvasElement,
  maxChars: number
): string | null {
  for (const q of [0.8, 0.65, 0.5, 0.35]) {
    const url = canvas.toDataURL("image/jpeg", q);
    if (url.startsWith("data:image/jpeg") && url.length <= maxChars) return url;
  }
  return null;
}
